import { useContext, useEffect, useState } from "react";
import { useCreate, useCustom } from "@pankod/refine-core";
import {
  Form,
  Input,
  useForm,
  Button,
  Typography,
  Select,
  Row,
  Col,
  Checkbox,
  Grid,
} from "@pankod/refine-antd";

import "../../../../styles/style.less";

import { ISub_Department } from "interfaces/sub_department";
import {
  CREATE_SUB_DEPARTMENT_API,
  CUSTOMER_API,
  DEPARTMENT_API,
  STATE_API,
} from "api/baseApi";
import { IDepartment } from "interfaces/department";
import { ICheckboxChangeActive } from "interfaces";
import { UserContext } from "hooks/useContext/useUserContext";
import { axiosInstance } from "providers/axios";
import { EAction, EStatusNotification, MSG_NOTIFICATION } from "constants/constant";
import { openNotification  } from "utils";
import { BaseOptionType, DefaultOptionType } from "antd/es/select";
import { SubDepartmentsProps } from "./interface";

export const SubDepartmentCreate = (props: SubDepartmentsProps) => {
  const {
    isModalVisible,
    setIsModalVisible,
    department,
    activeDepartment,
    updateSuccess,
  } = props;

  const breakpoint = Grid.useBreakpoint();
  const isMobile = !breakpoint.md;
  const [messageErr, setMessageErr] = useState<ISub_Department>();
  const [departmentData, setDepartmentData] = useState<ISub_Department>();

  const { customerId } = useContext(UserContext);
  const { formProps, form } = useForm<ISub_Department>({
    action: EAction.CREATE,
  });

  const { data: statesData } = useCustom({
    url: STATE_API.getAll,
    method: "get",
  });

  const { data: customerData } = useCustom({
    url: CUSTOMER_API.getOne + `/${customerId}`,
    method: "get",
  });

  useEffect(() => {
    (async () => {
      if (department) {
        const departmentData = await axiosInstance.get(
          DEPARTMENT_API.getOne + `/${department}`
        );
        setDepartmentData(departmentData?.data);
      } else {
        setDepartmentData(undefined);
      }
    })();
  }, [department]);

  const { mutate, data: createData, isLoading } = useCreate();

  const onFinish = (event: ISub_Department) => {
    mutate(
      {
        resource: CREATE_SUB_DEPARTMENT_API,
        values: {
          name: event.name,
          departmentId: event.departmentId,
          contactName: event.contactName,
          accessCode: event.accessCode,
          email: event.email,
          street: event.street,
          suiteNumber: event.suiteNumber,
          stateId: event.stateId,
          city: event.city,
          zipCode: event.zipCode,
          phoneNumber: event.phoneNumber,
          ext: event.ext,
          isActive: event.isActive,
        },
      },
      {
        onError: (error) => {
          openNotification(MSG_NOTIFICATION.ERROR, error.message, EStatusNotification.ERROR, 2);
        },
      }
    );
  };

  const onCheck = (event: ICheckboxChangeActive) => {
    if (event.target.checked) {
      form.setFieldsValue({ isActive: true });
    } else {
      form.setFieldsValue({ isActive: false });
    }
  };

  const { setFields } = form;

  useEffect(() => {
    form.resetFields();
    setFields([
      {
        name: "customerId",
        value: customerData?.data?.name,
      },
      {
        name: "departmentId",
        value: departmentData?.id ?? "",
      },
    ]);
  }, [form, isModalVisible, setFields, departmentData, customerData]);

  const [departmentCode, setDepartmentCode] = useState();
  const onSelect = (departmentId: string, option: BaseOptionType) => {
    (async () => {
      const department = await axiosInstance.get(
        DEPARTMENT_API.getOne + `/${departmentId}`
      );

      setDepartmentCode(department?.data?.accessCode);

      form.setFieldsValue({
        departmentCode: department?.data?.accessCode,
      });
    })();
  };

  useEffect(() => {
    if (createData?.data.status === 201) {
      updateSuccess();
      form.resetFields();
      openNotification(MSG_NOTIFICATION.SUCCESSFUL, createData?.data?.message, EStatusNotification.SUCCESS);
      setIsModalVisible(false);
      setMessageErr(messageErr);
    }
  }, [createData, form, messageErr, setIsModalVisible]);

  return (
    <Form
      {...formProps}
      layout="vertical"
      onFinish={(event) => onFinish(event as ISub_Department)}
    >
      <Row gutter={16}>
        <Col className="gutter-row" span={isMobile ? 24 : 12}>
          {" "}
          <Form.Item
            label="Customer"
            name="customerId"
            rules={[
              {
                required: false,
                message: "Please enter name",
              },
            ]}
            initialValue={customerId}
          >
            <Input disabled />
          </Form.Item>
          {messageErr?.customerId && (
            <Typography.Text type="danger">
              {messageErr.customerId[0]}
            </Typography.Text>
          )}
          <Form.Item
            label="Access Code"
            name="accessCodeCus"
            rules={[
              {
                required: false,
                message: "Please enter Access Code",
              },
            ]}
            initialValue={customerData?.data?.accessCode}
          >
            <Input disabled />
          </Form.Item>
          {messageErr?.accessCode && (
            <Typography.Text type="danger">
              {messageErr.accessCode[0]}
            </Typography.Text>
          )}
        </Col>
        <Col className="gutter-row" span={isMobile ? 24 : 12}>
          <Form.Item
            label="Department"
            name="departmentId"
            rules={[
              {
                required: true,
                message: "Please select departmentId",
              },
            ]}
            initialValue={departmentData?.name ?? ""}
          >
            <Select
              optionFilterProp="children"
              filterOption={(input, option: BaseOptionType | undefined) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              onSelect={onSelect}
            >
              {activeDepartment?.map((item: IDepartment, index: number) => (
                <Select.Option key={index} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {messageErr?.departmentId && (
            <Typography.Text type="danger">
              {messageErr.departmentId}
            </Typography.Text>
          )}

          <Form.Item
            label="Department Code"
            name="departmentCode"
            rules={[
              {
                required: false,
                message: "Please select Department Code",
              },
            ]}
            initialValue={
              department
                ? departmentData?.accessCode
                : departmentCode
                ? departmentCode
                : ""
            }
          >
            <Input disabled />
          </Form.Item>
          {messageErr?.departmentCode && (
            <Typography.Text type="danger">
              {messageErr.departmentCode}
            </Typography.Text>
          )}
        </Col>
      </Row>
      <Form.Item
        label="Sub-department Name"
        name="name"
        rules={[
          {
            required: true,
            message: "Please enter Sub-department Name",
          },
        ]}
        normalize={(value, prevVal, prevVals) => value.trimStart()}
      >
        <Input maxLength={50} />
      </Form.Item>
      {messageErr?.name && (
        <Typography.Text type="danger">{messageErr.name[0]}</Typography.Text>
      )}
      <Row gutter={16}>
        <Col className="gutter-row" span={isMobile ? 24 : 12}>
          <Form.Item
            label="Contact Name"
            name="contactName"
            rules={[
              {
                required: true,
                message: "Please enter Contact Name",
              },
            ]}
            normalize={(value, prevVal, prevVals) => value.trimStart()}
          >
            <Input maxLength={200} />
          </Form.Item>
          {messageErr?.contactName && (
            <Typography.Text type="danger">
              {messageErr.contactName[0]}
            </Typography.Text>
          )}

          <Form.Item
            label="Email Address"
            name="email"
            rules={[
              {
                required: true,
                type: "email",
                message: "Please enter a valid email address",
              },
            ]}
            normalize={(value, prevVal, prevVals) => value.trim()}
          >
            <Input maxLength={50} />
          </Form.Item>
          {messageErr?.email && (
            <Typography.Text type="danger">
              {messageErr.email[0]}
            </Typography.Text>
          )}

          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[
              {
                required: true,
                pattern: /^(?:\d*)$/,
                message: "Must contain 10 numbers only",
              },
            ]}
            normalize={(value, prevVal, prevVals) => value.trim()}
          >
            <Input maxLength={10} />
          </Form.Item>
          {messageErr?.phoneNumber && (
            <Typography.Text type="danger">
              {messageErr.phoneNumber[0]}
            </Typography.Text>
          )}

          <Form.Item
            label="Ext"
            name="ext"
            rules={[
              {
                pattern: /^(?:\d*)$/,
                message: "Please enter a valid Ext number!",
              },
            ]}
            normalize={(value, prevVal, prevVals) => value.trim()}
          >
            <Input maxLength={5} />
          </Form.Item>
          {messageErr?.ext && (
            <Typography.Text type="danger">{messageErr.ext[0]}</Typography.Text>
          )}

          <Form.Item
            label="Zip Code/Postal Code"
            name="zipCode"
            rules={[
              {
                required: true,
                message: "Please enter Zip Code",
              },
            ]}
            normalize={(value, prevVal, prevVals) => value.trim()}
          >
            <Input />
          </Form.Item>
          {messageErr?.zipCode && (
            <Typography.Text type="danger">
              {messageErr.zipCode[0]}
            </Typography.Text>
          )}
        </Col>
        <Col className="gutter-row" span={isMobile ? 24 : 12}>
          <Form.Item
            label="Street Address"
            name="street"
            rules={[
              {
                required: true,
                message: "Please enter Street Address",
              },
            ]}
            normalize={(value, prevVal, prevVals) => value.trimStart()}
          >
            <Input maxLength={200} />
          </Form.Item>
          {messageErr?.street && (
            <Typography.Text type="danger">
              {messageErr.street[0]}
            </Typography.Text>
          )}

          <Form.Item
            label="Suite Number"
            name="suiteNumber"
            rules={[
              {
                required: false,
                message: "Please enter Suite Number",
              },
            ]}
            normalize={(value, prevVal, prevVals) => value.trim()}
          >
            <Input maxLength={50} />
          </Form.Item>
          {messageErr?.suiteNumber && (
            <Typography.Text type="danger">
              {messageErr.suiteNumber[0]}
            </Typography.Text>
          )}

          <Form.Item
            label="City"
            name="city"
            rules={[
              {
                required: true,
                message: "Please enter City",
              },
            ]}
            normalize={(value, prevVal, prevVals) => value.trimStart()}
          >
            <Input maxLength={200} />
          </Form.Item>
          {messageErr?.city && (
            <Typography.Text type="danger">
              {messageErr.city[0]}
            </Typography.Text>
          )}

          <Form.Item
            label="State/Province"
            name="stateId"
            rules={[
              {
                required: true,
                message: "Please enter State/Province",
              },
            ]}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(
                input,
                option: BaseOptionType | DefaultOptionType | undefined
              ) =>
                option?.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              filterSort={(optionA, optionB) =>
                optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase())
              }
            >
              {statesData?.data?.map((item: IDepartment, index: number) => (
                <Select.Option key={index} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          {messageErr?.stateId && (
            <Typography.Text type="danger">
              {messageErr.stateId[0]}
            </Typography.Text>
          )}
          {department && (
            <>
              <Form.Item
                label="Sub-department Code"
                name="accessCode"
                rules={[
                  {
                    required: false,
                    message: "Please select Sub-department Code",
                  },
                ]}
                normalize={(value, prevVal, prevVals) => value.trim()}
              >
                <Input maxLength={50} />
              </Form.Item>
              {messageErr?.accessCode && (
                <Typography.Text type="danger">
                  {messageErr.accessCode}
                </Typography.Text>
              )}
            </>
          )}
        </Col>
      </Row>
      <Form.Item name="isActive" valuePropName="checked" initialValue={true}>
        <Checkbox
          onChange={(event) => {
            onCheck(event);
          }}
        >
          Is Active
        </Checkbox>
      </Form.Item>
      {messageErr?.isActive && (
        <Typography.Text type="danger">{messageErr.isActive}</Typography.Text>
      )}

      <div className={!isMobile ? "submit" : ""}>
        <Button type="primary" htmlType="submit" loading={isLoading}>
          Save
        </Button>
      </div>
    </Form>
  );
};
